import type { ReservationSpanPaymentLinkFilter } from '@repo/domain';
import {
  BusinessValidationCode,
  BusinessValidationError,
  useReservationSpanPaymentLinkActions,
} from '@repo/domain-service';
import { useEffect } from 'react';

import {
  SPAN_PAYMENT_LINK_CONFIRMATION_POLL_INTERVAL_MILLISECONDS,
  SPAN_PAYMENT_LINK_CONFIRMATION_TIMEOUT_MILLISECONDS,
} from '@features/reservation/constants/spanPaymentLink';

interface Params {
  filter: ReservationSpanPaymentLinkFilter;
  awaiting: boolean;
  onPaid: () => void;
  onFailed: () => void;
}

/**
 * Watch a BLIK payment started from the payment link settle: the link says `paid` once it is, refuses the link when
 * the span can no longer be paid, and a payment not confirmed while it stays valid has failed.
 */
export function useSpanPaymentLinkConfirmation({ filter, awaiting, onPaid, onFailed }: Params): void {
  const { fetchReservationSpanPaymentLink } = useReservationSpanPaymentLinkActions();

  useEffect(() => {
    if (!awaiting) return;

    let settled = false;
    const settle = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      callback();
    };

    const pollInterval = setInterval(() => {
      void (async () => {
        try {
          const link = await fetchReservationSpanPaymentLink(filter);
          if (link.status === 'paid') settle(onPaid);
        } catch (error) {
          if (
            error instanceof BusinessValidationError &&
            error.code === BusinessValidationCode.reservationSpanPaymentLinkInvalid
          ) {
            settle(onFailed);
          }
          // Any other failure is retried on the next tick; the timeout below ends the wait.
        }
      })();
    }, SPAN_PAYMENT_LINK_CONFIRMATION_POLL_INTERVAL_MILLISECONDS);

    const confirmationTimeout = setTimeout(() => settle(onFailed), SPAN_PAYMENT_LINK_CONFIRMATION_TIMEOUT_MILLISECONDS);

    return () => {
      settled = true;
      clearInterval(pollInterval);
      clearTimeout(confirmationTimeout);
    };
    // Runs only when the wait starts; the callbacks and the fetch are recreated each render and must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, filter.reservationSpanId, filter.signature]);
}
